import { makeScreensGraph } from '@util/FlowUtils';
import React, { ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { PathProgress } from 'ts-utils';
import { appOverride } from '../../util/AppUtils';
import { FlowConfig } from '../../config/FlowTypes';
import { Header } from './Header';
import { Progress } from './Progress';

type Props<ScreenNames extends string> = {
  showProgress: boolean;
  config: FlowConfig<ScreenNames>;
  currentScreen: ScreenNames;
};

export const ProgressHeader = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { screens } = props.config;
  const flowGraph = useMemo(() => makeScreensGraph(screens), [screens]);

  /**
   * Calculates the progress of the current screen through all screens using a graph.
   * @returns Progress as a decimal between `0` and `1`, or undefined.
   */
  const progress = useMemo<number | undefined>(
    function calculateProgress() {
      const progress = flowGraph.progress({
        first: screens[0].screenName,
        position: props.currentScreen,
      });
      return displayValueForProgress(progress);
    },
    [flowGraph, screens, props.currentScreen]
  );

  /** +1 to show non-zero progress on first screen (e.g. step 0 of 4 would be 0%, but adding 1, step 1 of 5 is 20%) */
  function displayValueForProgress(progress: PathProgress | undefined): number | undefined {
    return progress !== undefined ? (progress.progress + 1) / (progress.total + 1) : undefined;
  }

  return (
    <Header>
      {props.showProgress && progress !== undefined && (
        <ProgressWrapper>
          <Progress percent={progress * 100} showInfo={false} strokeWidth={5} />
        </ProgressWrapper>
      )}
    </Header>
  );
};

const ProgressWrapper = styled.div`
  width: 100%;
  max-width: 100%;

  ${appOverride({
    nerva: `
    @media (min-width: 600px) {
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -50vw;
      margin-right: -50vw;
      width: 100vw;
      max-width: 100vw;
      display: flex;
      justify-content: center;
    }
  `,
    default: '',
  })}
`;
